import axios from "axios";
import { geocodingCache } from "../utils/GeocodingCache";

export type Location = [lat: number, lng: number];

export interface Event {
  title: string;
  date: {
    start_date?: string;
    when?: string;
  };
  address: string | string[];
  description?: string;
  link: string;
  coordinates?: Location | null;
  venue?: {
    name: string;
    rating?: number;
  };
  ticket_info?: {
    price?: string;
    link: string;
  };
}

export class GeocoderUtility {
  private static readonly KNOWN_LOCATIONS: Record<string, Location> = {
    // Example Petaluma venue
    "mystic theatre": [38.23279, -122.64012],
  };

  private static parseAddress(address: string | string[]): {
    fullAddress: string;
    venueName: string;
    streetAddress: string;
    streetNumber: string;
    streetName: string;
    cityState: string;
  } {
    let streetAddress: string = "";
    let venueName: string = "";
    let cityState: string = "";
    let streetNumber: string = "";
    let streetName: string = "";

    if (Array.isArray(address)) {
      const [first, last] = address;
      streetAddress = first;
      cityState = last;

      // Try to extract venue name and street address
      const parts = first.split(",");
      if (parts.length > 1) {
        venueName = parts[0].trim();
        streetAddress = parts[1].trim();
      }
    } else {
      const parts = address.split(",").map((part) => part.trim());
      streetAddress = parts[0];
      cityState = parts.slice(-2).join(", ");

      if (parts.length > 2) {
        venueName = parts[0];
        streetAddress = parts[1];
      }
    }

    // Try to extract street number and name
    const streetParts = streetAddress.split(" ");
    if (streetParts[0]?.match(/^\d+$/)) {
      streetNumber = streetParts[0];
      streetName = streetParts.slice(1).join(" ");
    }

    return {
      fullAddress: Array.isArray(address) ? address.join(", ") : address,
      venueName,
      streetAddress,
      streetNumber,
      streetName,
      cityState,
    };
  }

  private static checkKnownLocation(venueString: string): Location | null {
    const normalizedVenue = venueString.toLowerCase().trim();

    for (const [knownVenue, coordinates] of Object.entries(
      this.KNOWN_LOCATIONS
    )) {
      if (normalizedVenue.includes(knownVenue)) {
        return coordinates;
      }
    }

    return null;
  }

  private static async makeGeocodingRequest(
    query: string
  ): Promise<Location | null> {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&countrycodes=us&addressdetails=1`
      );

      if (response.data?.[0]) {
        return [
          parseFloat(response.data[0].lat),
          parseFloat(response.data[0].lon),
        ];
      }
      return null;
    } catch (error) {
      console.error("Geocoding request error:", error);
      return null;
    }
  }

  private static async geocodeAddress(
    address: string | string[]
  ): Promise<Location | null> {
    // Normalize the address for caching
    const fullAddress = Array.isArray(address) ? address.join(", ") : address;

    // Check cache first
    const cachedResult = geocodingCache.get(fullAddress);
    if (cachedResult) {
      return cachedResult;
    }

    const {
      fullAddress: parsedFullAddress,
      venueName,
      streetAddress,
      streetNumber,
      streetName,
      cityState,
    } = this.parseAddress(address);

    let result: Location | null = null;

    // Try all strategies until one works
    if (venueName) {
      result = this.checkKnownLocation(venueName);
    }

    if (!result) {
      result = await this.makeGeocodingRequest(parsedFullAddress);
    }

    if (!result && streetAddress) {
      result = await this.makeGeocodingRequest(
        `${streetAddress}, ${cityState}`
      );
    }

    if (!result && streetNumber && streetName) {
      result = await this.makeGeocodingRequest(
        `${streetNumber} ${streetName}, ${cityState}`
      );
    }

    if (!result && streetName) {
      result = await this.makeGeocodingRequest(`${streetName}, ${cityState}`);
    }

    if (!result) {
      result = await this.makeGeocodingRequest(`${cityState}, USA`);
    }

    // If any strategy succeeded, cache the result with the original full address
    if (result) {
      geocodingCache.set(fullAddress, result);
    } else {
      console.warn("All geocoding attempts failed for:", fullAddress);
    }

    return result;
  }

  public static async updateEventCoordinates(
    events: Event[]
  ): Promise<Event[]> {
    const eventsToUpdate = events.filter((event) => !event.coordinates);

    if (eventsToUpdate.length === 0) return events;

    const updatedEvents = [...events];

    for (let i = 0; i < updatedEvents.length; i++) {
      if (!updatedEvents[i].coordinates && updatedEvents[i].address) {
        const coordinates = await this.geocodeAddress(updatedEvents[i].address);
        if (coordinates) {
          updatedEvents[i] = { ...updatedEvents[i], coordinates };
        }
      }
    }

    return updatedEvents;
  }
}
